// src/pages/Home.js
import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";
import { getAuth, signOut } from "firebase/auth";

export default function Home() {
  const { user } = useAuth();

  // Core state
  const [premium, setPremium] = useState(false);
  const [quoteToggle, setQuoteToggle] = useState(true);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState("quote-fade");

  // Spin state
  const [canSpin, setCanSpin] = useState(false);
  const [spinResult, setSpinResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);

  // Countdown state
  const [nextSpinMs, setNextSpinMs] = useState(null);

  // Quotes (kept)
  const premiumQuotes = useMemo(
    () => [
      "Reflection turns experience into wisdom.",
      "Your future is built one small decision at a time.",
      "Discipline fuels success; motivation only starts it.",
      "Slow progress is still progress.",
      "Don’t rush the process, trust it.",
      "The best growth comes from honest self-reflection.",
      "Growth begins the moment you choose honesty with yourself.",
      "Tiny daily reflections create massive long-term change.",
      "You can’t improve what you refuse to look at.",
      "Awareness is the first step; consistent action is the second.",
      "Your journal is proof that you showed up for yourself.",
      "Some days you win, some days you learn, both move you forward.",
      "You don’t need a perfect plan, just a willing mind.",
      "Your habits quietly design the life you live.",
      "Clarity comes when you slow down enough to listen.",
      "Who you become is built in the moments nobody sees.",
      "Progress is built in ordinary days, not just motivated ones.",
      "Be patient with yourself, real change is rarely dramatic.",
      "Your future self is grateful for every reflection you write today.",
      "You are allowed to rewrite your story, starting now."
    ],
    []
  );

  /* ---------------------------
     Firestore user sync (kept)
  ---------------------------- */
  useEffect(() => {
    if (!user) return;

    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();

      setPremium(!!data.premium);
      setQuoteToggle(
        typeof data.quoteToggle === "boolean" ? data.quoteToggle : true
      );

      if (data.lastSpinAt) {
        const last = data.lastSpinAt.toDate().getTime();
        const unlockAt = last + 24 * 60 * 60 * 1000;
        const diff = unlockAt - Date.now();

        if (diff <= 0) {
          setCanSpin(true);
          setNextSpinMs(null);
        } else {
          setCanSpin(false);
          setNextSpinMs(diff);
        }
      } else {
        setCanSpin(true);
        setNextSpinMs(null);
      }
    });

    return () => unsub();
  }, [user]);

  /* ---------------------------
     Countdown tick (every minute)
  ---------------------------- */
  useEffect(() => {
    if (!nextSpinMs) return;

    const interval = setInterval(() => {
      setNextSpinMs((prev) => {
        if (!prev) return null;
        const updated = prev - 60000;
        return updated <= 0 ? null : updated;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [nextSpinMs]);

  // When countdown hits 0, make sure button unlocks visually
  useEffect(() => {
    if (nextSpinMs === null) {
      // Do not force true if Firestore still says locked, but this keeps UI responsive.
      // Firestore snapshot will be the source of truth.
      // If it is truly unlocked, canSpin will already be true.
    }
  }, [nextSpinMs]);

  /* ---------------------------
     Quote rotation (kept)
  ---------------------------- */
  useEffect(() => {
    if (!premium || !quoteToggle) return;

    const interval = setInterval(() => {
      setFadeClass("quote-fade-out");
      setTimeout(() => {
        setQuoteIndex((i) => (i + 1) % premiumQuotes.length);
        setFadeClass("quote-fade-in");
      }, 300);
    }, 6000);

    return () => clearInterval(interval);
  }, [premium, quoteToggle, premiumQuotes.length]);

  useEffect(() => {
    if (!premium || !quoteToggle) return;
    const t = setTimeout(() => setFadeClass("quote-fade"), 300);
    return () => clearTimeout(t);
  }, [quoteIndex, premium, quoteToggle]);

  /* ---------------------------
     Helpers
  ---------------------------- */
  const polarPosition = (angleDeg, radius) => {
    // 0deg is up, 90deg right, 180deg down, 270deg left
    const rad = (angleDeg * Math.PI) / 180;
    return {
      left: `calc(50% + ${Math.sin(rad) * radius}px)`,
      top: `calc(50% - ${Math.cos(rad) * radius}px)`
    };
  };

  const formatCountdown = (ms) => {
    if (!ms || ms <= 0) return "";
    const totalMinutes = Math.ceil(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) return `Next spin in ${hours}h ${minutes}m`;
    return `Next spin in ${minutes}m`;
  };

  /* ---------------------------
     Spin logic (the working version)
     Visual wheel slices:
     0-90    Premium (locked)
     90-180  Nothing
     180-270 Reflection
     270-360 Bonus Prompt
     Pointer at top (0deg).
     To land on centerAngle, rotate wheel by (360 - centerAngle).
  ---------------------------- */
  const handleSpin = async () => {
    if (!user || !canSpin || spinning) return;

    setSpinning(true);
    setSpinResult(null);

    // Premium intentionally excluded
    const outcomes = [
      { label: "Nothing", centerAngle: 135 },
      { label: "Reflection", centerAngle: 225 },
      { label: "Bonus Prompt", centerAngle: 315 }
    ];

    const selected = outcomes[Math.floor(Math.random() * outcomes.length)];
    const spins = 4 * 360;
    const landingRotation = spins + (360 - selected.centerAngle);

    setRotation(landingRotation);

    setTimeout(async () => {
      setSpinResult(selected.label);
      setCanSpin(false);

      await updateDoc(doc(db, "users", user.uid), {
        lastSpinAt: new Date()
      });

      setSpinning(false);
    }, 3000);
  };

  /* ---------------------------
     Logout (kept)
  ---------------------------- */
  const handleLogout = async () => {
    const auth = getAuth();
    await signOut(auth);
    window.location.href = "/login";
  };

  // If no user logged in
  if (!user) {
    return (
      <div className="home-center">
        <h2>Welcome to ReflectRise</h2>
        <p>Please sign in to begin your journey 🌟</p>
      </div>
    );
  }

  // Counter-rotate text so it stays upright while wheel spins
  const textCounterRotation = -rotation;

  return (
    <div className="home-container" style={{ textAlign: "center" }}>
      <div
        className="home-card"
        style={{
          background: "linear-gradient(180deg, #151220, #0e0b16)",
          color: "var(--text)",
          padding: "2rem",
          borderRadius: "18px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          maxWidth: "600px",
          margin: "3rem auto"
        }}
      >
        <h2 style={{ marginTop: 0 }}>Welcome back 👋</h2>

        {/* DAILY SPIN */}
        <div style={{ margin: "2.5rem 0" }}>
          <h3 style={{ marginBottom: "1rem" }}>🎡 Daily Spin</h3>

          <div
            style={{
              position: "relative",
              width: "260px",
              height: "260px",
              margin: "1.5rem auto"
            }}
          >
            {/* Pointer */}
            <div
              style={{
                position: "absolute",
                top: "-10px",
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent",
                borderBottom: "16px solid #fff",
                zIndex: 5
              }}
            />

            {/* ROTATING GROUP (slices + premium border + labels) */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                transform: `rotate(${rotation}deg)`,
                transition: spinning
                  ? "transform 3s cubic-bezier(0.22,1,0.36,1)"
                  : "none"
              }}
            >
              {/* Base wheel slices */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background:
                    "conic-gradient(" +
                    "#121212 0deg 90deg," + // Premium
                    "#2a2a2a 90deg 180deg," + // Nothing
                    "var(--primary) 180deg 270deg," + // Reflection
                    "var(--primary-2) 270deg 360deg)", // Bonus Prompt
                  boxShadow: "inset 0 0 30px rgba(0,0,0,0.9)"
                }}
              />

              {/* Premium gold BORDER arc only (spins with wheel) */}
              <div
                style={{
                  position: "absolute",
                  inset: "-4px",
                  borderRadius: "50%",
                  background:
                    "conic-gradient(gold 0deg 90deg, transparent 90deg 360deg)",
                  filter: "drop-shadow(0 0 6px rgba(255,215,0,0.6))",
                  pointerEvents: "none",
                  WebkitMask:
                    "radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 6px))",
                  mask:
                    "radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 6px))"
                }}
              />

              {/* Labels placed in slice centers, move with wheel, stay upright */}
              {[
                { text: "Premium", angle: 45, color: "gold", weight: 700 },
                { text: "Nothing", angle: 135, color: "#eee", weight: 600 },
                { text: "Reflection", angle: 225, color: "#fff", weight: 600 },
                {
                  text: "Bonus Prompt",
                  angle: 315,
                  color: "#f2e9ff",
                  weight: 600
                }
              ].map((item) => {
                const pos = polarPosition(item.angle, 70);
                return (
                  <div
                    key={item.text}
                    style={{
                      position: "absolute",
                      ...pos,
                      transform: `translate(-50%, -50%) rotate(${textCounterRotation}deg)`,
                      color: item.color,
                      fontWeight: item.weight,
                      fontSize: "1rem",
                      textShadow: "0 2px 6px rgba(0,0,0,0.85)",
                      userSelect: "none",
                      pointerEvents: "none"
                    }}
                  >
                    {item.text}
                  </div>
                );
              })}
            </div>
          </div>

          <button
            onClick={handleSpin}
            disabled={!canSpin || spinning}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              border: "none",
              background: canSpin ? "var(--primary)" : "#444",
              color: "#fff",
              cursor: canSpin ? "pointer" : "not-allowed",
              minWidth: "220px"
            }}
          >
            {canSpin ? (spinning ? "Spinning..." : "Spin the Wheel") : "Come back tomorrow"}
          </button>

          {!canSpin && nextSpinMs && (
            <div
              style={{
                marginTop: "0.6rem",
                fontSize: "0.9rem",
                color:
                  nextSpinMs < 60 * 60 * 1000
                    ? "var(--primary)"
                    : "rgba(255,255,255,0.6)"
              }}
            >
              {formatCountdown(nextSpinMs)}
            </div>
          )}

          {spinResult && (
            <p style={{ marginTop: "1rem", fontWeight: 500 }}>
              {spinResult === "Nothing" &&
                "Every reflection counts. Come back tomorrow 🌱"}
              {spinResult === "Reflection" &&
                "You are building clarity one thought at a time ✨"}
              {spinResult === "Bonus Prompt" &&
                "Today is a great day to explore a deeper question 📝"}
            </p>
          )}
        </div>

        {/* Quote toggle */}
        {premium && (
          <div
            onClick={() =>
              updateDoc(doc(db, "users", user.uid), {
                quoteToggle: !quoteToggle
              })
            }
            style={{
              cursor: "pointer",
              marginBottom: "0.75rem",
              userSelect: "none"
            }}
          >
            {quoteToggle ? "Hide Motivational Quotes" : "Show Motivational Quotes"}
          </div>
        )}

        {/* Quote box */}
        {premium && quoteToggle && (
          <div
            className={`quote-box ${fadeClass}`}
            style={{
              background:
                "linear-gradient(135deg, var(--primary), var(--primary-2))",
              padding: "1.2rem",
              borderRadius: "12px",
              margin: "1.5rem 0",
              color: "#fff",
              fontSize: "1.2rem",
              fontWeight: 500
            }}
          >
            “{premiumQuotes[quoteIndex]}”
          </div>
        )}

        <button
          onClick={handleLogout}
          style={{
            marginTop: "1.5rem",
            background: "#333",
            color: "#fff",
            border: "none",
            padding: "8px 16px",
            borderRadius: "8px",
            cursor: "pointer"
          }}
        >
          Log Out
        </button>
      </div>
    </div>
  );
}
